import React from 'react';
import BiomarkerRangeBar from '../member/BiomarkerRangeBar';
import { getBiomarker } from '../../data/biomarkerCatalogue';
import { computeBiomarkerStatus } from '../../utils/biomarkerStatus';
import type { FeedbackBiomarkerCard as CardData } from '../../types/feedbackChat';

/**
 * A biomarker result rendered inside a chat bubble.
 *
 * THE MODEL NEVER WROTE ANY OF THESE NUMBERS. It emitted a marker key; the
 * value, unit, range and in/out verdict are all computed here, client-side,
 * through exactly the same catalogue and status functions the rest of the
 * member UI uses. Two consequences, both intentional:
 *
 *   1. The model cannot misstate a result, because it never held one.
 *   2. This card cannot disagree with the range bar the member sees on their
 *      results page, because it is literally the same component fed by the same
 *      computation — not a second implementation that could drift.
 *
 * When the marker has no reference range (the explanation-only ones —
 * haematocrit, MPV, the differential percentages) `status.hasBar` is false and
 * no bar renders. The explanation still does, which is the whole point of those
 * entries existing.
 */

interface Props {
  card: CardData;
}

export default function FeedbackBiomarkerCard({ card }: Props) {
  const meta = getBiomarker(card.key);
  if (!meta) return null;

  const status = computeBiomarkerStatus(
    { valueNumber: card.value, unit: card.unit },
    meta,
  );

  return (
    <div
      className="rounded-2xl p-4 mt-3"
      style={{
        backgroundColor: 'var(--melio-cream-warm)',
        border: '1px solid var(--melio-border)',
      }}
    >
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <span className="text-sm font-semibold" style={{ color: 'var(--melio-ink)' }}>
          {meta.label}
        </span>
        {card.collectedAt && (
          <span className="text-[11px]" style={{ color: 'var(--melio-text-muted)' }}>
            {formatDate(card.collectedAt)}
          </span>
        )}
      </div>

      {status.hasBar ? (
        <BiomarkerRangeBar meta={meta} status={status} />
      ) : (
        <p className="text-sm mb-2" style={{ color: 'var(--melio-text-muted)' }}>
          {card.value != null
            ? `${card.value}${card.unit ? ` ${card.unit}` : ''}`
            : 'No result recorded.'}
          {' '}
          <span style={{ color: 'var(--melio-text-muted)' }}>
            — there is no standard reference range for this one, so your lab&rsquo;s
            own printed range is the one to go by.
          </span>
        </p>
      )}

      {card.explanation && (
        <p className="text-sm leading-relaxed mt-3" style={{ color: 'var(--melio-ink)' }}>
          {card.explanation}
        </p>
      )}

      <p className="text-[11px] mt-3" style={{ color: 'var(--melio-text-muted)' }}>
        This shows what the marker measures and where your result sits. What it means
        for you is a conversation for your care team.
      </p>
    </div>
  );
}

function formatDate(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
